import React from 'react'
import { connect } from 'react-redux'
import { createEntityAdapter } from '@reduxjs/toolkit'
import debounce from 'lodash/debounce'
import delay from 'lodash/delay'
import get from 'lodash/get'
import uniqueId from 'lodash/uniqueId'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { Loader } from '@manychat/manyui'

import * as flowPlayerActions from 'apps/flowPlayer/actions/flowPlayerActions'
import ControlsPanel from 'apps/flowPlayer/components/ControlsPanel'
import Logo from 'apps/flowPlayer/components/Logo'
import Placeholder from 'apps/flowPlayer/components/Placeholder'
import { PlayerFrame } from 'apps/flowPlayer/components/PlayerFrame/PlayerFrame'
import { ScreenshotFrame } from 'apps/flowPlayer/components/ScreenshotFrame/ScreenshotFrame'
import { currentMessageTemplateSlice } from 'apps/whatsApp/redux/currentMessageTemplateSlice'
import { messageTemplateSlice } from 'apps/whatsApp/redux/messageTemplatesSlice'
import { statsSlice } from 'apps/whatsApp/redux/statsSlice'
import app from 'common/builder/components/chart/FlowChart/app'
import { NodeType } from 'common/builder/constants/NodeType'
import FlowChartViewer from 'common/builder/controllers/chart/FlowChartViewer'
import batchAdapter from 'common/builder/models/Batch/adapter'
import nodeRegistry from 'common/builder/nodeRegistry'
import { setTitle } from 'common/core'
import { getCsrfToken } from 'common/core/selectors/appSelectors'
import { parseAvailableFields } from 'common/fields/api/adapter'
import { parseFlow } from 'common/flow/models/Flow/parseFlow'
import { getResizeObserverEntrySize } from 'utils/getResizeObserverEntrySize'
import { RoutePaths } from 'utils/router/constants'
import { getQuery, isPathnameMatchRoute } from 'utils/router/tools'
import errorTrackingService from 'utils/services/errorTrackingService'

let resolveFirstRender
const waitFirstRender = new Promise((r) => (resolveFirstRender = r))
let resolveStageReady
const waitStageReady = new Promise((r) => (resolveStageReady = r))

// Storage for flow data
let mockData

// Function for flow rendering, initialized when component mounted, used in getPreview
let renderFlow

class FlowPlayerPage extends React.Component {
  state = { flowData: null, viewportSize: { width: 0, height: 0 } }
  static propTypes = {
    fetchSharedFlow: PropTypes.func,
  }

  static defaultProps = {
    fetchSharedFlow: () => {},
  }

  onResize = debounce((entries) => {
    if (entries.length === 0) {
      return
    }

    const viewportSize = getResizeObserverEntrySize(entries[0])

    if (!viewportSize) {
      return
    }

    this.setState({ viewportSize })
  }, 500)

  resizeObserver = new ResizeObserver(this.onResize)

  setWrapperRef = (ref) => {
    this.clearResizeObserver()
    this.wrapperRef = ref
    this.setResizeObserver()
  }

  setResizeObserver = () => {
    if (!this.wrapperRef) {
      return
    }

    this.resizeObserver.observe(this.wrapperRef)
  }

  clearResizeObserver = () => {
    this.resizeObserver.disconnect()
  }

  mockStore({ data, builderId, csrfToken }) {
    const query = getQuery()
    const screenshotMode = query['screenshot_mode']
    mockData = getStoreState({
      data,
      builderId,
      isScreenshotMode: screenshotMode,
      csrfToken,
    })

    if (this.storeMocked) {
      return
    }

    const store = window.app.store
    this._storeDispatch = store.dispatch
    this._storeGetState = store.getState

    store.dispatch = () => {}
    store.getState = () => Object.assign({}, this._storeGetState(), mockData)
    this.storeMocked = true
  }

  componentDidMount() {
    const query = getQuery()
    const shareHash = query['share_hash']
    const screenshotMode = query['screenshot_mode']
    if (screenshotMode) {
      setTitle('flow-preview-service')
      renderFlow = this.onRenderFlow
      resolveFirstRender()
    } else {
      this.props.fetchSharedFlow(shareHash)
    }
  }

  componentDidUpdate = () => {
    const { data } = this.props
    const query = getQuery()
    const screenshotMode = query['screenshot_mode']

    waitStageReady.then(() => {
      this.state.resolve && this.state.resolve()
    })

    if (!screenshotMode && data) {
      const { flow } = data
      setTitle(flow.name)
    }
  }

  componentWillUnmount() {
    this.clearResizeObserver()

    if (this.storeMocked) {
      const store = window.app.store
      store.dispatch = this._storeDispatch
      store.getState = this._storeGetState
    }
  }

  onRenderFlow = async (flowData) => {
    /**
     * Before every flow render we need to clear flowData state as this will remount FlowChartViewer component and reset all nodes.
     * This is necessary to avoid issues and errors with using redux selectors as we are the mocking store.
     */
    await new Promise((resolve) => this.setState({ flowData: null }, resolve))
    return new Promise((resolve) => {
      this.setState({ flowData, resolve })
    })
  }

  handleStageReady = () => resolveStageReady()

  render() {
    const { flowData } = this.state
    const { data: propsFlowData, error, csrfToken, screenshot_mode: screenshotMode } = this.props
    const data = flowData || propsFlowData

    if (error) {
      return <Placeholder ref={() => {}} />
    }

    if (!data) {
      return <Loader center />
    }

    const { flow } = data
    const builderId = uniqueId(flow.ns)
    this.mockStore({ data, builderId, csrfToken })

    const withFrame = !screenshotMode && !isPathnameMatchRoute(RoutePaths.FLOW_PLAYER_EMBED)
    const Component = withFrame ? PlayerFrame : ScreenshotFrame

    return (
      <Component ref={this.setWrapperRef}>
        <FlowChartViewer
          key={flow.ns}
          builderId={builderId}
          flowId={flow.ns}
          onStageReady={this.handleStageReady}
          viewportSize={this.state.viewportSize}
          player
        />
        {!screenshotMode && (
          <>
            <ControlsPanel {...getControlsProps(data)} builderId={builderId} />
            <Logo />
          </>
        )}
      </Component>
    )
  }
}

window.mcFlowPlayerApp = {
  getPreview: async (options = { thumbnail: false }) => {
    // eslint-disable-next-line no-useless-catch
    try {
      await waitFirstRender
      await renderFlow(window.mcFlowPlayerData)
    } catch (err) {
      errorTrackingService.trackError(err, {
        fingerprint: 'flow-player-data-request',
      })
    }

    await app.ensureTexture()
    console.log('Images loaded!')

    // without this delay, there will be a small padding around the scene
    await new Promise((resolve) => delay(() => resolve(), 500))
    const stageBounds = app.root.stage.getBounds()
    const bounds = {
      x: (stageBounds.x - app.transform.x) / app.transform.k,
      y: (stageBounds.y - app.transform.y) / app.transform.k,
      width: stageBounds.width / app.transform.k,
      height: stageBounds.height / app.transform.k,
    }

    const exportOptions = {
      retina: true,
      padding: 100,
      maxOutputSize: null,
      outputSize: null,
    }
    const thumbnailExportOptions = {
      padding: 50,
      retina: true,
      maxOutputSize: null,
      outputSize: { width: 955 / 2, height: 500 / 2 },
    }
    const opt = options.thumbnail ? thumbnailExportOptions : exportOptions
    const renderTexture = app.getImage(bounds, opt.outputSize, opt)
    const canvas = app.root.renderer.extract.canvas(renderTexture)
    return canvas.toDataURL('image/png')
  },
}

function getControlsProps(data) {
  const { flow } = data
  //------ DATA for Controls ------//
  return {
    name: flow.name,
    sharingEnabled: flow.shared_cloning_enabled,
    previewUrl: data.preview_url,
    cloningUrl: data.cloning_url,

    avatarUrl: data.account.avatar,
    pageName: data.account.page_name,
  }
}

function getStoreState({ data, builderId, isScreenshotMode, csrfToken }) {
  if (!data) {
    return
  }

  const { flow } = data
  const tags = data.tags_list.map((tag) => ({
    ...tag,
    id: tag.tag_id,
    label: tag.tag_normalized || tag.tag_name || tag.name,
  }))
  const sequences = data.sequences_list.map((sequence) => ({
    id: sequence.sequence_id,
    ...sequence,
  }))
  const widgets = data.widgets_list.map((widget) => ({
    ...widget,
    id: widget.tag_id,
    label: widget.tag_normalized || widget.tag_name || widget.name,
  }))

  const userFields = data.custom_fields_list.map((field) => ({
    id: field.field_id,
    ...field,
  }))

  const otns = get(data, 'otn_reasons_list', []).map((otn) => ({
    ...otn,
    id: otn.reason_id,
    label: otn.caption,
  }))

  const globalFields = data.global_fields_list.map((field) => ({
    id: field.field_id,
    ...field,
  }))

  const zaps = data.zapier_events.map((zap) => ({
    ...zap,
    id: zap.event_id,
    label: zap.event_normalized || zap.event_name,
  }))

  const smartSegments = data.smart_segments_list.map((smartSegment) => ({
    id: smartSegment.segment_id,
    label: smartSegment.title,
    ...smartSegment,
  }))

  const segments = [
    ...otns.map((otn) => ({ ...otn, id: `one_time_notification_${otn.id}` })),
    ...tags.map((tag) => ({ ...tag, id: `tag_${tag.id}` })),
    ...sequences.map((sequence) => ({
      ...sequence,
      id: `sequence_${sequence.id}`,
      label: sequence.name,
    })),
    ...widgets.map((widget) => ({ ...widget, id: `widget_${widget.id}` })),
    ...smartSegments.map((smartSegment) => ({
      ...smartSegment,
      id: `smart_segment_${smartSegment.id}`,
    })),
  ]
  const externalFlows = data.external_flows.map(({ ns, name }) => ({ id: ns, name }))
  const preparedExternalFlows = getEntity(externalFlows)
  const parsedFlow = parseFlow({ ...flow, draft_batch: null })

  const botTimeZone = data.account?.timezone ?? 'UTC'
  const parsed = batchAdapter.parseBatch(
    {
      contents: [...flow.contents],
      root_content: flow.contentId,
    },
    { botTimeZone },
  )

  const startingStepNode = nodeRegistry
    .getByType(NodeType.STARTING_STEP)
    .create({ id: NodeType.STARTING_STEP })
  startingStepNode.targetId = flow.root_content_id
  parsed.nodes.push(startingStepNode)

  const flowData = { externalTriggers: { byId: {} } }
  for (let prop of Object.keys(parsed)) {
    const entity = parsed[prop]
    if (Array.isArray(entity)) {
      flowData[prop] = getEntity(entity)
    }
  }

  const whatsAppState = {
    emsu: {},
    disconnectChannel: {},
    messageTemplates: { ...messageTemplateSlice.getInitialState() },
    currentMessageTemplate: { ...currentMessageTemplateSlice.getInitialState() },
    createMessageTemplateOnBroadcast: {},
    broadcastOnboarding: {},
    stats: statsSlice.getInitialState(),
    broadcasting: {},
    catalog: {},
    cartReply: {},
    coexistence: {},
  }

  if ('whatsapp_message_template_languages' in data && 'whatsapp_message_template_packs' in data) {
    whatsAppState.messageTemplates.languages = {
      ...whatsAppState.messageTemplates.languages,
      data: data.whatsapp_message_template_languages,
    }
    whatsAppState.messageTemplates.list = {
      ...whatsAppState.messageTemplates.list,
      data: data.whatsapp_message_template_packs,
    }
  }

  return {
    app: {
      csrf_token: csrfToken,
      currentAccount: {
        id: flow.page_id,
        user_roles_enabled: false,
        installed_applications: data.used_applications,
        timezone: botTimeZone,
        instagram_channel: {},
      },
      user: {
        skip_preview_onboarding_modal: true,
      },
    },
    availableFields: createEntityAdapter().setAll(
      { ids: [], entities: {} },
      parseAvailableFields(data.available_fields, true),
    ),
    flow: {
      ...preparedExternalFlows,
      byId: {
        [flow.ns]: parsedFlow,
        ...preparedExternalFlows.byId,
      },
    },

    //------ DATA for ActionGroups ------//
    zaps: getEntity(zaps),
    tags: getEntity(tags),
    otns: getEntity(otns),
    oneTimeNotifyReasons: getEntity(otns),
    sequence: {
      ...getEntity(sequences),
      listFetched: true,
    },
    userFields: getEntity(userFields),
    globalFields: getEntity(globalFields),

    //------ DATA for Conditions ------//
    segments: getEntity(segments),
    filter: {
      fields: data.available_fields,
    },

    smartSegments: getEntity(smartSegments),

    //------ DATA for FlowChartPlayer ------//
    builders: {
      byId: {
        [builderId]: {
          ...flowData,
          state: {
            coordinates: Array.isArray(flow.coordinates) ? {} : flow.coordinates,
            rootId: flow.root_content_id,
            selectedNodes: {},
            transform: {},
            isStaticTextResolution: isScreenshotMode || false,
          },
        },
      },
    },
    ui: {
      previewModal: {
        isOpen: false,
        keyword: '',
      },
    },

    //------ Additional DATA ------//

    whatsApp: whatsAppState,
  }
}

function getEntity(items) {
  return {
    items,
    byId: items.reduce((r, i) => ((r[i.id] = i), r), {}),
  }
}

const FlowPlayerPageWithStore = connect(
  (state) => ({ ...state.flowPlayer, csrfToken: getCsrfToken(state) }),
  (dispatch) => bindActionCreators(flowPlayerActions, dispatch),
)(FlowPlayerPage)

/**
 * @public page component
 */
export { FlowPlayerPageWithStore as Component }
